import { parseISO, format } from "date-fns";
import { useState, useEffect } from "react";

import { useHandleBusinessSchedule } from "0.Mine/1.Hooks/business/openingTimes";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { DaySchedule } from "0.Mine/3.Interfaces/Schedule";

export type DayScheduleWithPeriod = DaySchedule & {
  openPeriod: "both" | "morning" | "afternoon";
};

export type ScheduleState = Record<string, DayScheduleWithPeriod>;

export const useBusinessScheduleLogic = () => {
  const { session } = useBusinessContext();
  const business = session?.selectedBusiness;
  const [vacationStart, setVacationStart] = useState<Date | null>(null);
  const [vacationEnd, setVacationEnd] = useState<Date | null>(null);
  const { createBusinessSchedule, getBusinessSchedule } = useHandleBusinessSchedule();
  const [schedule, setSchedule] = useState<ScheduleState>({
    monday: {
      isOpen: true,
      isContinuous: false,
      morningOpen: "09:00",
      morningClose: "11:30",
      afternoonOpen: "14:30",
      afternoonClose: "19:00",
      openPeriod: "both",
    },
    tuesday: {
      isOpen: true,
      isContinuous: false,
      morningOpen: "09:00",
      morningClose: "11:30",
      afternoonOpen: "14:30",
      afternoonClose: "19:00",
      openPeriod: "both",
    },
    wednesday: {
      isOpen: true,
      isContinuous: false,
      morningOpen: "09:00",
      morningClose: "11:30",
      afternoonOpen: "14:30",
      afternoonClose: "19:00",
      openPeriod: "both",
    },
    thursday: {
      isOpen: true,
      isContinuous: false,
      morningOpen: "09:00",
      morningClose: "11:30",
      afternoonOpen: "14:30",
      afternoonClose: "19:00",
      openPeriod: "both",
    },
    friday: {
      isOpen: true,
      isContinuous: false,
      morningOpen: "09:00",
      morningClose: "11:30",
      afternoonOpen: "14:30",
      afternoonClose: "19:00",
      openPeriod: "both",
    },
    saturday: {
      isOpen: true,
      isContinuous: false,
      morningOpen: "09:00",
      morningClose: "11:30",
      afternoonOpen: "14:30",
      afternoonClose: "19:00",
      openPeriod: "both",
    },
    sunday: {
      isOpen: false,
      isContinuous: false,
      morningOpen: "09:00",
      morningClose: "11:30",
      afternoonOpen: "14:30",
      afternoonClose: "19:00",
      openPeriod: "both",
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!business) return;
      const response = await getBusinessSchedule(business.id);
      if (response && !Array.isArray(response)) {
        const updatedSchedule = Object.entries(response).reduce((acc, [day, dayData]) => {
          acc[day] = { ...dayData, openPeriod: (dayData as any).openPeriod ?? "both" };
          return acc;
        }, {} as ScheduleState);
        setSchedule(updatedSchedule);
      }
    };

    if (business?.id) {
      fetchData();
    }
  }, [business]);

  // Convert time strings to minutes for easier comparison
  const timeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const handleDayToggle = (day: string) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], isOpen: !prev[day].isOpen },
    }));
  };

  const handleContinuousToggle = (day: string) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], isContinuous: !prev[day].isContinuous },
    }));
  };

  const handleTimeChange = (day: string, period: string, value: string) => {
    const currentSchedule = schedule[day];
    let isValid = true;

    const valueInMinutes = timeToMinutes(value);

    switch (period) {
      case "morningOpen":
        if (valueInMinutes < 0 || valueInMinutes > timeToMinutes("12:59")) {
          alert("Morning opening time must be between 00:00 and 12:59");
          isValid = false;
        } else if (
          currentSchedule.morningClose &&
          valueInMinutes >= timeToMinutes(currentSchedule.morningClose)
        ) {
          alert("Opening time must be earlier than closing time");
          isValid = false;
        }
        break;
      case "morningClose":
        if (valueInMinutes < 0 || valueInMinutes > timeToMinutes("12:59")) {
          alert("Morning closing time must be between 00:00 and 12:59");
          isValid = false;
        } else if (
          currentSchedule.morningOpen &&
          valueInMinutes <= timeToMinutes(currentSchedule.morningOpen)
        ) {
          alert("Closing time must be later than opening time");
          isValid = false;
        }
        break;
      case "afternoonOpen":
        if (valueInMinutes < timeToMinutes("13:00") || valueInMinutes > timeToMinutes("23:59")) {
          alert("Afternoon opening time must be between 13:00 and 23:59");
          isValid = false;
        } else if (
          currentSchedule.afternoonClose &&
          valueInMinutes >= timeToMinutes(currentSchedule.afternoonClose)
        ) {
          alert("Opening time must be earlier than closing time");
          isValid = false;
        }
        break;
      case "afternoonClose":
        if (valueInMinutes < timeToMinutes("13:00") || valueInMinutes > timeToMinutes("23:59")) {
          alert("Afternoon closing time must be between 13:00 and 23:59");
          isValid = false;
        } else if (
          currentSchedule.afternoonOpen &&
          valueInMinutes <= timeToMinutes(currentSchedule.afternoonOpen)
        ) {
          alert("Closing time must be later than opening time");
          isValid = false;
        }
        break;
    }

    if (isValid) {
      setSchedule((prev) => ({
        ...prev,
        [day]: { ...prev[day], [period]: value },
      }));
    }
  };

  const handlePeriodChange = (day: string, value: "both" | "morning" | "afternoon") => {
    setSchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], openPeriod: value },
    }));
  };

  const handleDateChange = (type: "start" | "end", value: Date | null) => {
    if (type === "start") {
      setVacationStart(value);
    } else {
      setVacationEnd(value);
    }
  };

  const submit = async () => {
    // Validate all time ranges before submitting
    for (const [day, daySchedule] of Object.entries(schedule)) {
      if (daySchedule.isOpen) {
        if (
          daySchedule.openPeriod !== "afternoon" &&
          daySchedule.morningOpen > daySchedule.morningClose
        ) {
          alert(`Invalid morning time range for ${day}`);
          return;
        }
        if (
          daySchedule.openPeriod !== "morning" &&
          daySchedule.afternoonOpen > daySchedule.afternoonClose
        ) {
          alert(`Invalid afternoon time range for ${day}`);
          return;
        }
      }
    }

    if (!business) {
      return;
    }
    await createBusinessSchedule(schedule, business.id);
  };

  return {
    schedule,
    vacationStart,
    vacationEnd,
    handleDayToggle,
    handleContinuousToggle,
    handleTimeChange,
    handlePeriodChange,
    handleDateChange,
    submit,
    parseISO,
    format,
  };
};
